import type { LearnTopic } from "../topic-types";

/**
 * Understanding Your Triggers.
 *
 * Educational, trauma-informed content about triggers, trigger mapping,
 * and building a personal coping plan.
 */
export const TRIGGERS_TOPIC: LearnTopic = {
  slug: "triggers",
  emoji: "🎯",
  title: "Understanding Your Triggers",
  tagline: "From reaction to recognition",
  theme: "sage",
  category: "nervous-system",
  isNew: true,
  seoDescription:
    "A trauma-informed, plain-English guide to understanding triggers — internal and external, emotional and physical — with a trigger mapping exercise and a printable personal coping plan from Recovery Trauma™.",
  cardBlurb: "Understand what triggers you, spot the pattern early, and build a coping plan.",
  intro: [
    "A trigger is anything — a sound, a look, a word, a memory, even a feeling in your own body — that sets off a strong reaction rooted in past experience, often faster and bigger than the present moment seems to call for. Triggers are not a sign of weakness; they are the nervous system doing exactly what it learned to do, sometimes long ago.",
    "Learning to recognise your own triggers, and the pattern that tends to follow them, can turn a confusing reaction into something more understandable — and more workable. This is not about eliminating triggers altogether, which usually isn't realistic, but about building enough awareness and support that you have more choice in how you respond.",
    "This guide moves from understanding, to recognising patterns, to two practical tools: a trigger map and a personal coping plan. Go at your own pace, and feel free to skip anything that doesn't feel useful right now.",
  ],
  closing:
    "Getting to know your triggers is an ongoing, gentle process rather than a one-off task. If triggers are connected to frightening or overwhelming memories, working through this alongside a trauma-informed therapist can help it feel safer and more manageable.",
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand",
      blurb: "What a trigger is, and why they can feel so much bigger than the moment.",
      intro:
        "Plain-English education first. Nothing here asks you to look inward yet — you can simply read.",
      resources: [
        {
          slug: "what-is-a-trigger",
          kind: "learn",
          title: "What is a trigger?",
          summary: "A present-moment cue that activates a past survival response.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "A trigger is something in the present that the nervous system associates with a past experience of danger, fear, or distress — even if the current situation is actually safe. When triggered, the body and mind can respond as though the original threat is happening again, right now.",
            },
            {
              type: "list",
              title: "Some ideas people find helpful",
              items: [
                "Triggers are not overreactions — they are old alarms responding to a new cue that resembles the old one",
                "The reaction is often faster than conscious thought, because it starts in the body's threat-detection system",
                "A trigger doesn't have to make logical sense to be real and valid",
                "Recognising a trigger is the first step towards having more choice around it",
              ],
            },
            {
              type: "note",
              text: "This page is educational, not a diagnostic tool. If triggers are frequent, intense or disruptive, a trauma-informed therapist can help you work through them safely.",
            },
          ],
        },
        {
          slug: "external-triggers",
          kind: "learn",
          title: "External triggers",
          summary: "Cues in the outside world — people, places, sounds, situations.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "External triggers come from outside you — things you can see, hear, smell or encounter in the world. They often resemble some detail of a past difficult experience, even a small one.",
            },
            {
              type: "list",
              title: "Examples people commonly recognise",
              items: [
                "A particular tone of voice, raised or otherwise",
                "A specific place, room, or type of building",
                "Certain smells, sounds, or pieces of music",
                "A date, anniversary, or time of year",
                "Being in a crowd, or conversely, being alone",
                "Certain phrases or words used in an argument",
              ],
            },
          ],
        },
        {
          slug: "internal-triggers",
          kind: "learn",
          title: "Internal triggers",
          summary: "Cues from within — thoughts, memories, sensations, feelings.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Internal triggers arise from inside you rather than the outside world — a thought, a memory, a body sensation, or even a feeling that reminds the nervous system of a past experience.",
            },
            {
              type: "list",
              title: "Examples people commonly recognise",
              items: [
                "A racing heartbeat that reminds the body of past fear",
                "A memory surfacing unexpectedly",
                "A feeling of being trapped, even in an open space",
                "Physical tiredness, hunger or illness lowering your capacity to cope",
                "A thought like 'I've done something wrong' or 'I'm about to be judged'",
              ],
            },
            {
              type: "note",
              text: "Internal triggers can be harder to notice because they don't have an obvious external cause. Naming them is often the biggest step towards recognising them next time.",
            },
          ],
        },
        {
          slug: "emotional-triggers",
          kind: "learn",
          title: "Emotional triggers",
          summary: "Feelings that seem to arrive out of proportion to the moment.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "Emotional triggers occur when a feeling — often shame, fear, rejection, or abandonment — appears suddenly and with an intensity that feels disproportionate to what is actually happening. This is usually because the current feeling has connected with an older, unresolved one.",
            },
            {
              type: "list",
              title: "This might look like",
              items: [
                "Feeling intensely rejected by a minor, ambiguous comment",
                "A wave of shame after a small mistake",
                "Sudden anger that feels much bigger than the situation warrants",
                "A rush of panic when someone seems distant or delayed in replying",
              ],
            },
          ],
        },
        {
          slug: "body-sensations",
          kind: "learn",
          title: "Body sensations as triggers and signals",
          summary: "How the body often knows before the mind catches up.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "The body often registers a trigger before conscious awareness does. Learning to notice physical sensations can give you an earlier warning than waiting for a thought or a feeling to fully form.",
            },
            {
              type: "list",
              title: "Common body signals",
              items: [
                "A tightening in the chest or throat",
                "Shallow or held breath",
                "A racing or pounding heart",
                "Clenched jaw, fists, or shoulders",
                "A hollow or unsettled feeling in the stomach",
                "Sudden heat, cold, or a flushed feeling",
              ],
            },
            {
              type: "note",
              text: "Noticing these signals is not about controlling them instantly — it's about building familiarity, so they become useful information rather than a surprise.",
            },
          ],
        },
        {
          slug: "trigger-response-behaviour",
          kind: "learn",
          title: "Trigger → response → behaviour",
          summary: "How one moment can cascade into a pattern of reacting.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "A useful way to understand triggers is as the first step in a chain: a trigger occurs, the nervous system responds (often fight, flight, freeze or fawn), and then a behaviour follows from that response. Understanding this chain can help you find places to intervene gently.",
            },
            {
              type: "flow",
              title: "A common chain",
              items: [
                "Trigger — a cue, internal or external",
                "Body response — heart rate, breath, muscle tension shift",
                "Emotional response — fear, shame, anger, panic",
                "Survival response — fight, flight, freeze, or fawn",
                "Behaviour — what you say or do as a result",
              ],
            },
            {
              type: "prose",
              text: "The gap between the trigger and the behaviour is often where the most useful change can happen — not by skipping the reaction, but by noticing it a little sooner each time.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise",
      blurb: "Spotting early warning signs before a trigger takes full hold.",
      intro:
        "This section is about building familiarity with your own patterns — gently, and without judgement.",
      resources: [
        {
          slug: "early-warning-signs",
          kind: "learn",
          title: "Recognising early warning signs",
          summary: "The small cues that often arrive just before a bigger reaction.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Most trigger responses have an early stage — small, often subtle signals — before they build into a fuller reaction. Learning to spot these early can create a small window for choice.",
            },
            {
              type: "list",
              title: "Common early warning signs",
              items: [
                "A sudden shift in mood without an obvious reason",
                "Your breathing becoming shallow or held",
                "An urge to leave, go quiet, or check out",
                "A specific phrase or thought repeating in your mind",
                "Irritability or a short temper appearing quickly",
                "A subtle sense of dread or unease",
              ],
            },
            {
              type: "note",
              text: "You don't need to catch every trigger early — this is a practice that develops gradually, not something to master overnight.",
            },
          ],
        },
        {
          slug: "noticing-my-patterns",
          kind: "reflect",
          title: "Noticing my patterns",
          summary: "Reflecting on recent triggers and what followed.",
          minutes: 6,
          blocks: [
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "What was the most recent moment I felt triggered?",
                "What happened just before — externally and internally?",
                "What did my body do first?",
                "What did I do next, and did that pattern feel familiar?",
                "Looking back, is there an earlier point where I might have noticed something building?",
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise",
      blurb: "A trigger mapping exercise and grounding practice.",
      intro:
        "These practices are optional, and you are always welcome to pause or stop. Go at whatever pace feels manageable.",
      resources: [
        {
          slug: "trigger-mapping-exercise",
          kind: "practice",
          title: "The Trigger Map exercise",
          summary: "A guided way of tracing a recent trigger from start to finish.",
          minutes: 10,
          blocks: [
            {
              type: "prose",
              text: "Trigger mapping means slowly tracing a recent reaction backwards, from the behaviour to the very first signal, so the whole chain becomes visible. This can make future reactions feel more understandable and less sudden.",
            },
            {
              type: "steps",
              title: "How to map a trigger",
              items: [
                { label: "Choose one recent moment", note: "Pick something manageable to start with, not the hardest thing you can think of." },
                { label: "Name the behaviour", note: "What did you actually do or say?" },
                { label: "Trace the feeling", note: "What emotion was present just before the behaviour?" },
                { label: "Trace the body sensation", note: "What did you notice physically, and when did it start?" },
                { label: "Identify the trigger", note: "What was the cue — internal or external — that started this chain?" },
                { label: "Look for the echo", note: "Does this trigger connect to something familiar or older?" },
              ],
            },
            {
              type: "note",
              text: "If this exercise brings up strong feelings, it's completely fine to pause, ground yourself, and come back another time — or not to continue with it at all.",
            },
          ],
        },
        {
          slug: "grounding-when-triggered",
          kind: "practice",
          title: "Grounding when triggered",
          summary: "A short practice for the moment a trigger hits.",
          minutes: 5,
          blocks: [
            {
              type: "steps",
              title: "In the moment",
              items: [
                { label: "Pause", note: "Even a few seconds of pausing can interrupt the automatic chain." },
                { label: "Feel your feet on the ground", note: "A simple, physical anchor to the present." },
                { label: "Name what's happening", note: "Silently or aloud: 'I'm having a trigger response right now.'" },
                { label: "Slow one breath out", note: "A longer exhale can help settle the nervous system." },
                { label: "Remind yourself of the date and place", note: "This helps distinguish then from now." },
              ],
            },
            {
              type: "note",
              text: "Our Grounding and Breathing tools offer more guided practices if this is helpful to you.",
            },
          ],
        },
      ],
    },
    {
      slug: "reflect",
      emoji: "✍️",
      title: "Reflect",
      blurb: "Printable worksheets for mapping triggers and planning ahead.",
      intro:
        "These worksheets are for your own private use. Take your time, and only fill in what feels manageable today.",
      resources: [
        {
          slug: "trigger-map-worksheet",
          kind: "worksheet",
          title: "My Trigger Map",
          summary: "A printable worksheet for tracing a trigger from cue to behaviour.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "Use this worksheet alongside, or instead of, the Trigger Map exercise — whichever suits you best.",
            },
          ],
          worksheetFields: [
            { label: "The trigger", hint: "What happened — internally or externally — right before the reaction?", lines: 3 },
            { label: "Body sensations", hint: "What did you notice physically, and where?", lines: 3 },
            { label: "Emotional response", hint: "What feeling arose, and how intense was it?", lines: 3 },
            { label: "Behaviour that followed", hint: "What did you say or do as a result?", lines: 3 },
            { label: "Does this feel familiar?", hint: "Does it connect to an older experience or pattern?", lines: 3 },
          ],
        },
        {
          slug: "personal-coping-plan",
          kind: "worksheet",
          title: "My personal coping plan",
          summary: "A printable plan for responding to triggers with more choice.",
          minutes: 8,
          blocks: [
            {
              type: "prose",
              text: "A coping plan works best when it's written in a calm moment, ready to refer to when things feel harder. There's no need to get it perfect — you can update it any time.",
            },
            {
              type: "note",
              text: "This plan is a personal tool, not a substitute for professional support. If triggers are connected to frightening memories or feel unmanageable, please consider reaching out to a trauma-informed therapist, or our Crisis Resources page if you're in immediate distress.",
            },
          ],
          worksheetFields: [
            { label: "My common triggers", hint: "Situations, people, sensations or thoughts that tend to set things off.", lines: 4 },
            { label: "My early warning signs", hint: "What do I notice first, in my body or mood?", lines: 3 },
            { label: "What helps me in the moment", hint: "Grounding techniques, phrases, or actions that help.", lines: 4 },
            { label: "People I can reach out to", hint: "Who can I contact if I need support?", lines: 3 },
            { label: "What I want to remember afterwards", hint: "A kind reminder to yourself for after a difficult moment.", lines: 3 },
          ],
        },
      ],
    },
  ],
  continueTo: [
    { label: "Emotional Flashbacks", to: "/learn/emotional-flashbacks" },
    { label: "Trauma Responses", to: "/learn/trauma-responses" },
    { label: "Try a grounding exercise", to: "/grounding" },
    { label: "Try a breathing exercise", to: "/breathing" },
    { label: "Write in your journal", to: "/journal" },
  ],
};
